/* eslint-disable no-magic-numbers */
import {
  RULES_MUL_DIV_DATA,
  RULES_MUL_DIV_EIGHTS,
  RULES_MUL_DIV_NINES,
  RULES_MUL_DIV_OTHERS,
  RULES_MUL_DIV_SEVENS,
  RULES_MUL_DIV_SIXES,
} from '../../games/data/vifRulesMulDivData';

import type { ProblemPool } from './deal';
import {
  createRandomInt,
  isFirstTime,
  loadPool,
  markVisited,
  zeros,
} from './rulesProblemState';

/**
 * Problem-pool selection for all sixteen `VIFRulesMulDiv` variants
 * (gameFlags 0-8 multiplication, 10-16 division; there is no flag 9 -
 * `computeOperation` is `gameFlag <= 9 ? MUL : DIV`).
 *
 * This is a direct port of `VIFRulesMulDiv.populateProblem`'s per-flag
 * branches with the engine's `this` state lifted into an explicit
 * `RulesMulDivProblemState`, mirroring `rulesAddSubProblems.ts`. Mul/Div adds three
 * mechanics add/sub never needed: a persistent-across-layers pool with a
 * "give back on retry" rule (flag 3 round 1), pair de-duplication within a
 * layer (flags 11/13), and an answer-bucketed anti-clustering rotation
 * (flag 16).
 */

export const RULES_MUL_DIV_FLAGS = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 10, 11, 12, 13, 14, 15, 16,
] as const;

export type RulesMulDivFlag = (typeof RULES_MUL_DIV_FLAGS)[number];

export function isRulesMulDivFlag(flag: number): flag is RulesMulDivFlag {
  return (RULES_MUL_DIV_FLAGS as readonly number[]).includes(flag);
}

/** Mutable pool/memory carried across layers, mirroring the engine fields. */
export interface RulesMulDivProblemState {
  /** Legacy `topEQs`. */
  topEQs: string[];
  /** Legacy `bottomAnswers`. */
  bottomAnswers: string[];
  /** Legacy `blnChosen` - per-layer "already used" memory. */
  blnChosen: number[][];
  /** Legacy `blnFirstTimeLayer`. */
  blnFirstTimeLayer: number[];
  /** Legacy `pool1`/`pool1Answ`/`pool1Used`. Persist across a round's layers. */
  pool1: string[];
  pool1Answ: string[];
  pool1Used: number[];
  /** Legacy `pool2`/`pool2Answ`/`pool2Used`. */
  pool2: string[];
  pool2Answ: string[];
  pool2Used: number[];
  /**
   * Legacy `poolUsedThisRound` - indices drawn *this round only*, for flag 3
   * round 1's "give back on retry" rule: if layer 1 restarts after a mistake,
   * those specific indices are unmarked in `pool1Used`/`pool2Used` rather than
   * staying burned for the rest of the round.
   */
  poolUsedThisRound: { pool1: number[]; pool2: number[] };
  /**
   * Legacy `checkPairs` - raw `(v1, v2)` operand pairs already rendered this
   * layer, for flags 11/13's de-duplication. Reset per layer.
   */
  checkPairs: Array<readonly [number, number]>;
  /**
   * Legacy `fixedRounds` - flag 16's fixed layer-rotation for a round, seeded
   * once on layer 1.
   */
  fixedRounds: number[];
}

export function createRulesMulDivProblemState(): RulesMulDivProblemState {
  return {
    topEQs: [],
    bottomAnswers: [],
    blnChosen: [[0], [0], [0], [0]],
    blnFirstTimeLayer: [1, 1, 1, 1],
    pool1: [],
    pool1Answ: [],
    pool1Used: [],
    pool2: [],
    pool2Answ: [],
    pool2Used: [],
    poolUsedThisRound: { pool1: [], pool2: [] },
    checkPairs: [],
    fixedRounds: [],
  };
}

export interface PopulateArgs {
  flag: RulesMulDivFlag;
  /** Legacy `intLayer`, 1-based. */
  layer: number;
  /** Legacy `gameFrame.round`, 1-based. */
  round: number;
  /** Legacy `intColumns`, permanently 3. */
  columns: number;
  state: RulesMulDivProblemState;
  rng?: () => number;
  /** Matches the shape of `Chance.randomInt(min, max)` (inclusive bounds). */
  randomInt?: (min: number, max: number) => number;
}

/**
 * Advances `state` to the pool for the given flag/layer/round and returns it.
 * Mutates `state` in place, exactly as the legacy engine does.
 */
export function populateRulesMulDivProblems({
  flag,
  layer,
  round,
  state,
  rng = Math.random,
  randomInt = createRandomInt(rng),
}: PopulateArgs): ProblemPool {
  // Unlike AddSub, no MulDiv flag's board size varies with `intColumns` -
  // every generation loop below is a fixed 9-cell board, so `columns` (kept
  // on `PopulateArgs` only for interface parity with AddSub) is unused here.
  switch (flag) {
    case 0:
      populateFlag0(layer, round, state, randomInt);
      break;
    case 1:
      populateFlag1(layer, round, state, randomInt);
      break;
    case 2:
      populateFlag2(layer, round, state);
      break;
    case 3:
      populateFlag3(layer, round, state, randomInt);
      break;
    case 4:
      populateFlag4(layer, round, state, randomInt);
      break;
    case 5:
      populateFlag5(layer, round, state, randomInt);
      break;
    case 6:
      populateFlag6(layer, round, state, randomInt);
      break;
    case 7:
      populateFlag7(layer, state);
      break;
    case 8:
      populateFlag8(layer, state);
      break;
    case 10:
      populateFlag10(layer, round, state, randomInt);
      break;
    case 11:
      populateFlag11(layer, round, state, randomInt);
      break;
    case 12:
      populateFlag12(layer, round, state);
      break;
    case 13:
      populateFlag13(layer, round, state, randomInt);
      break;
    case 14:
      populateFlag14(layer, round, state, randomInt);
      break;
    case 15:
      populateFlag15(layer, round, state, randomInt);
      break;
    case 16:
      populateFlag16(layer, round, state, randomInt);
      break;
  }

  return {
    topEQs: state.topEQs,
    bottomAnswers: state.bottomAnswers,
  };
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/**
 * The common "one layer, one static data-file table" shape shared by most
 * flags here (0's layer 4, all of 1/2/4/14's rounds 1-2, 10's layer 4 and
 * round 2 layer 2, all of 12/16's rounds 1-2). `chosenLength` is the pool
 * size for this layer - not the board size - matching legacy `blnChosen`.
 */
function populateStaticLayerTable(
  state: RulesMulDivProblemState,
  layer: number,
  table: ProblemPool,
  chosenLength: number,
): void {
  if (isFirstTime(state, layer)) {
    loadPool(state, table);
    markVisited(state, layer);
  }
  state.blnChosen[layer - 1] = zeros(chosenLength);
}

/**
 * Draws `count` distinct indices in `[0, maxIndex]` without replacement via
 * rejection sampling. Ported from the `a = Chance.randomInt(...); while
 * (used[a]) ...` loops that recur across flags 0/1/4/10.
 */
function drawDistinct(
  count: number,
  maxIndex: number,
  used: number[],
  randomInt: (min: number, max: number) => number,
): number[] {
  const drawn: number[] = [];
  for (let p = 0; p < count; p++) {
    let a = randomInt(0, maxIndex);
    while (used[a] == 1) {
      a = randomInt(0, maxIndex);
    }
    used[a] = 1;
    drawn.push(a);
  }
  return drawn;
}

/**
 * Appends four zero-answer facts to the pool, picked without repetition from
 * a small (5-entry) inline literal list. Shared by flags 0 (`n × 0`/`0 × n`)
 * and 10 (`0 ÷ n`) - both push a literal `'0'` answer regardless of which
 * fact was picked.
 */
function appendZeroPicks(
  state: RulesMulDivProblemState,
  arrZero: readonly string[],
  randomInt: (min: number, max: number) => number,
): void {
  const used = [0, 0, 0, 0, 0];
  for (const a of drawDistinct(4, 4, used, randomInt)) {
    state.topEQs.push(arrZero[a]!);
    state.bottomAnswers.push('0');
  }
}

/**
 * Flag 0 ("zero-one rule", multiplication): two rounds of four layers each.
 * Layers with a "mostly zero" table also splice in one extra non-zero fact
 * (picked from a small inline literal list) plus four *more* zero facts
 * (picked without repetition from another inline list) - both lists are
 * inline in the legacy method itself, not the data file
 * (`VIFRulesMulDiv.ts:510-669`).
 *
 * Legacy quirk, preserved verbatim: round 1 layer 1's extra non-zero pick
 * pairs `tmpEQs[a]` with `state.bottomAnswers[a]` - the *table's own* answer
 * at index `a`, not a matching answer for `tmpEQs[a]` - unlike every other
 * layer here, which uses its own dedicated `tmpAns` array
 * (`VIFRulesMulDiv.ts:516-522`).
 */
function populateFlag0(
  layer: number,
  round: number,
  state: RulesMulDivProblemState,
  randomInt: (min: number, max: number) => number,
): void {
  if (isFirstTime(state, layer)) {
    if (round == 1) {
      switch (layer) {
        case 1: {
          loadPool(state, RULES_MUL_DIV_DATA.flag0.round1.layer1);
          const a = randomInt(0, 3);
          state.topEQs.push(['2 × 1', '1 × 4', '6 × 1', '9 × 1'][a]!);
          state.bottomAnswers.push(state.bottomAnswers[a]!);
          appendZeroPicks(
            state,
            ['0 × 1', '0 × 3', '8 × 0', '5 × 0', '7 × 0'],
            randomInt,
          );
          break;
        }
        case 2: {
          loadPool(state, RULES_MUL_DIV_DATA.flag0.round1.layer2);
          const a = randomInt(0, 3);
          state.topEQs.push(['1 × 2', '4 × 1', '1 × 6', '1 × 9'][a]!);
          state.bottomAnswers.push(['2', '4', '6', '9'][a]!);
          appendZeroPicks(
            state,
            ['2 × 0', '0 × 4', '0 × 7', '6 × 0', '0 × 5'],
            randomInt,
          );
          break;
        }
        case 3: {
          loadPool(state, RULES_MUL_DIV_DATA.flag0.round1.layer3);
          const a = randomInt(0, 3);
          state.topEQs.push(['1 × 1', '9 × 1', '1 × 3', '6 × 1'][a]!);
          state.bottomAnswers.push(['1', '9', '3', '6'][a]!);
          appendZeroPicks(
            state,
            ['4 × 0', '0 × 0', '3 × 0', '0 × 9', '0 × 8'],
            randomInt,
          );
          break;
        }
        case 4:
          loadPool(state, RULES_MUL_DIV_DATA.flag0.round1.layer4);
          break;
      }
    } else if (round == 2) {
      switch (layer) {
        case 1: {
          loadPool(state, RULES_MUL_DIV_DATA.flag0.round2.layer1);
          const a = randomInt(0, 3);
          state.topEQs.push(['1 × 9', '4 × 1', '2 × 1', '1 × 5'][a]!);
          state.bottomAnswers.push(['9', '4', '2', '5'][a]!);
          appendZeroPicks(
            state,
            ['0 × 4', '7 × 0', '0 × 0', '6 × 0', '2 × 0'],
            randomInt,
          );
          break;
        }
        case 2:
          loadPool(state, RULES_MUL_DIV_DATA.flag0.round2.layer2);
          break;
        case 3: {
          loadPool(state, RULES_MUL_DIV_DATA.flag0.round2.layer3);
          const a = randomInt(0, 3);
          state.topEQs.push(['0 × 8', '1 × 9', '3 × 0', '8 × 1'][a]!);
          state.bottomAnswers.push(['0', '9', '0', '8'][a]!);
          appendZeroPicks(
            state,
            ['0 × 2', '5 × 0', '0 × 6', '9 × 0', '0 × 5'],
            randomInt,
          );
          break;
        }
        case 4: {
          loadPool(state, RULES_MUL_DIV_DATA.flag0.round2.layer4);
          const a = randomInt(0, 3);
          state.topEQs.push(['4 × 1', '1 × 5', '1 × 6', '1 × 7'][a]!);
          state.bottomAnswers.push(['4', '5', '6', '7'][a]!);
          appendZeroPicks(
            state,
            ['8 × 0', '0 × 7', '0 × 9', '1 × 0', '4 × 0'],
            randomInt,
          );
          break;
        }
      }
    }
    markVisited(state, layer);
  }
  state.blnChosen[layer - 1] = zeros(12);
}

/**
 * Flag 1 ("ten by tens"): rounds 1-2 are four independent static layer
 * tables; rounds 3-4 draw 5+4 facts without replacement from two pools that
 * persist across the round's four layers (`VIFRulesMulDiv.ts:671-815`).
 */
function populateFlag1(
  layer: number,
  round: number,
  state: RulesMulDivProblemState,
  randomInt: (min: number, max: number) => number,
): void {
  const data = RULES_MUL_DIV_DATA.flag1;

  if (round == 1 || round == 2) {
    const byRound = round == 1 ? data.round1 : data.round2;
    const table = [
      byRound.layer1,
      byRound.layer2,
      byRound.layer3,
      byRound.layer4,
    ][layer - 1]!;
    populateStaticLayerTable(state, layer, table, 9);
    return;
  }

  populatePoolDraw5And4(
    state,
    layer,
    round == 3 ? data.round3 : data.round4,
    randomInt,
  );
}

/**
 * Shared by flag 1 rounds 3-4 and flag 4/14 rounds 3-4: initialise two pools
 * once per round (persisting across its four layers), then draw 5 from pool
 * 1 (41 entries, indices 0-40) and 4 from pool 2 (40 entries, indices 0-39)
 * without replacement, once per layer (`VIFRulesMulDiv.ts:737-813`,
 * `:1121-1197`).
 */
function populatePoolDraw5And4(
  state: RulesMulDivProblemState,
  layer: number,
  pools: {
    pool1: readonly string[];
    pool1Answers: readonly string[];
    pool2: readonly string[];
    pool2Answers: readonly string[];
  },
  randomInt: (min: number, max: number) => number,
): void {
  if (state.pool1Used.length == 0) {
    state.pool1Used = zeros(41);
    state.pool1 = [...pools.pool1];
    state.pool1Answ = [...pools.pool1Answers];
    state.pool2Used = zeros(40);
    state.pool2 = [...pools.pool2];
    state.pool2Answ = [...pools.pool2Answers];
  }

  if (isFirstTime(state, layer)) {
    state.topEQs = [];
    state.bottomAnswers = [];
    for (const a of drawDistinct(5, 40, state.pool1Used, randomInt)) {
      state.topEQs.push(state.pool1[a]!);
      state.bottomAnswers.push(state.pool1Answ[a]!);
    }
    for (const a of drawDistinct(4, 39, state.pool2Used, randomInt)) {
      state.topEQs.push(state.pool2[a]!);
      state.bottomAnswers.push(state.pool2Answ[a]!);
    }
    markVisited(state, layer);
  }
  state.blnChosen[layer - 1] = zeros(9);
}

/**
 * Flag 2 ("mirror rule", multiplication): two rounds of four independent
 * static layer tables each, no pools, no extras
 * (`VIFRulesMulDiv.ts:816-887`).
 */
function populateFlag2(
  layer: number,
  round: number,
  state: RulesMulDivProblemState,
): void {
  const data = RULES_MUL_DIV_DATA.flag2;
  const byRound = round == 1 ? data.round1 : data.round2;
  const table = [
    byRound.layer1,
    byRound.layer2,
    byRound.layer3,
    byRound.layer4,
  ][layer - 1]!;
  populateStaticLayerTable(state, layer, table, 12);
}

/**
 * Flag 3 ("double digits by single digits", multiplication): round 1 draws
 * from two persistent pools with a "give back on retry" rule and a
 * last-digit-match constraint; rounds 2-4 generate operands on the fly with
 * the same last-digit-match idea plus an anti-duplicate/anti-double check
 * (`VIFRulesMulDiv.ts:888-1022`).
 */
function populateFlag3(
  layer: number,
  round: number,
  state: RulesMulDivProblemState,
  randomInt: (min: number, max: number) => number,
): void {
  if (round == 1) {
    populateFlag3Round1(layer, round, state, randomInt);
  } else {
    populateFlag3RandomRounds(layer, round, state, randomInt);
  }

  state.blnFirstTimeLayer[layer - 1] = 0;
  state.blnChosen[layer - 1] = zeros(9);
}

function populateFlag3Round1(
  layer: number,
  round: number,
  state: RulesMulDivProblemState,
  randomInt: (min: number, max: number) => number,
): void {
  if (state.pool1Used.length == 0) {
    state.pool1Used = zeros(72);
    state.pool1 = [...RULES_MUL_DIV_DATA.flag3.round1.pool1];
    state.pool1Answ = [...RULES_MUL_DIV_DATA.flag3.round1.pool1Answers];
    state.pool2Used = zeros(54);
    state.pool2 = [...RULES_MUL_DIV_DATA.flag3.round1.pool2];
    state.pool2Answ = [...RULES_MUL_DIV_DATA.flag3.round1.pool2Answers];
  }

  // Re-entering after a mistake (not the layer's first visit): give back
  // this round's draws so far rather than treating them as permanently
  // spent - the retried board can redraw them.
  if (state.blnFirstTimeLayer[layer - 1] == 0) {
    for (const index of state.poolUsedThisRound.pool1) {
      state.pool1Used[index] = 0;
    }
    for (const index of state.poolUsedThisRound.pool2) {
      state.pool2Used[index] = 0;
    }
  }
  state.poolUsedThisRound = { pool1: [], pool2: [] };

  state.topEQs = [];
  state.bottomAnswers = [];
  const modNo = round >= 3 ? 4 : 2;
  let lastVal = 0;

  const nTop = 6 - layer;
  for (let p = 0; p < nTop; p++) {
    let a = randomInt(0, 71);
    while (state.pool1Used[a] == 1) {
      a = randomInt(0, 71);
    }
    if (p % modNo == 0) {
      lastVal = Number(state.pool1Answ[a]);
    } else {
      while (
        lastVal % 10 != Number(state.pool1Answ[a]) % 10 ||
        state.pool1Used[a] == 1
      ) {
        a = randomInt(0, 71);
      }
    }
    state.pool1Used[a] = 1;
    state.topEQs.push(state.pool1[a]!);
    state.bottomAnswers.push(state.pool1Answ[a]!);
    state.poolUsedThisRound.pool1.push(a);
  }

  for (let p = 0; p < 3 + layer; p++) {
    let a = randomInt(0, 53);
    while (state.pool2Used[a] == 1) {
      a = randomInt(0, 53);
    }
    if (p % modNo == 0 || p == 0) {
      lastVal = Number(state.pool2Answ[a]);
    } else {
      while (
        lastVal % 10 != Number(state.pool2Answ[a]) % 10 ||
        state.pool2Used[a] == 1
      ) {
        a = randomInt(0, 53);
      }
    }
    state.pool2Used[a] = 1;
    state.topEQs.push(state.pool2[a]!);
    state.bottomAnswers.push(state.pool2Answ[a]!);
    state.poolUsedThisRound.pool2.push(a);
  }
}

/**
 * Rounds 2-4: nine on-the-fly operand pairs, grouped by `modNo` so most
 * share the last digit of their product with the group's first ("seed")
 * product, and none is an exact duplicate or exact double/half of it.
 */
function populateFlag3RandomRounds(
  layer: number,
  round: number,
  state: RulesMulDivProblemState,
  randomInt: (min: number, max: number) => number,
): void {
  state.topEQs = [];
  state.bottomAnswers = [];

  const iMax = round == 2 ? 49 : round == 3 ? 79 : 99;
  let modNo = round >= 3 ? 4 : 2;
  let resMod10 = 0;
  let lastVal = 0;

  for (let p = 0; p < 9; p++) {
    let v1 = randomInt(10, iMax);
    let v2 = layer < 4 ? randomInt(2, layer + 4) : randomInt(2, 9);

    if (p % modNo == 0) {
      resMod10 = (v1 * v2) % 10;
      lastVal = v1 * v2;
    } else {
      while (
        (v1 * v2) % 10 != resMod10 ||
        lastVal == v1 * v2 ||
        (lastVal > v1 * v2 && lastVal > v1 * v2 * 2) ||
        (v1 * v2 > lastVal && v1 * v2 > lastVal * 2)
      ) {
        v1 = randomInt(10, iMax);
        v2 = layer < 4 ? randomInt(2, layer + 4) : randomInt(2, 9);
      }
    }

    if (round >= 3 && p > 4) {
      modNo = 99;
    }

    if (round == 2 || layer <= 2 || randomInt(0, 1) == 0) {
      state.topEQs.push(`${v1} × ${v2}`);
    } else {
      state.topEQs.push(`${v2} × ${v1}`);
    }
    state.bottomAnswers.push(String(v1 * v2));
  }
}

/**
 * Flags 4 and 14 share a body: rounds 1-2 are four independent static layer
 * tables (flag 14's `topEQs` come from its own data, but its
 * `bottomAnswers` always come from flag 4's - a commuted-operand trick,
 * `a × 10` and `10 × a` have the same product); rounds 3-4 draw 5+4 from two
 * pools shared verbatim between the two flags
 * (`VIFRulesMulDiv.ts:1023-1198`).
 */
function populateFlag4(
  layer: number,
  round: number,
  state: RulesMulDivProblemState,
  randomInt: (min: number, max: number) => number,
): void {
  populateFlag4Or14(4, layer, round, state, randomInt);
}

function populateFlag14(
  layer: number,
  round: number,
  state: RulesMulDivProblemState,
  randomInt: (min: number, max: number) => number,
): void {
  populateFlag4Or14(14, layer, round, state, randomInt);
}

function populateFlag4Or14(
  flag: 4 | 14,
  layer: number,
  round: number,
  state: RulesMulDivProblemState,
  randomInt: (min: number, max: number) => number,
): void {
  const flag4Data = RULES_MUL_DIV_DATA.flag4;

  if (round == 1 || round == 2) {
    const flag4ByRound = round == 1 ? flag4Data.round1 : flag4Data.round2;
    const flag4Table = [
      flag4ByRound.layer1,
      flag4ByRound.layer2,
      flag4ByRound.layer3,
      flag4ByRound.layer4,
    ][layer - 1]!;

    if (isFirstTime(state, layer)) {
      if (flag == 4) {
        loadPool(state, flag4Table);
      } else {
        const flag14Data = RULES_MUL_DIV_DATA.flag14;
        const flag14ByRound =
          round == 1 ? flag14Data.round1 : flag14Data.round2;
        const flag14Table = [
          flag14ByRound.layer1,
          flag14ByRound.layer2,
          flag14ByRound.layer3,
          flag14ByRound.layer4,
        ][layer - 1]!;
        state.topEQs = [...flag14Table.topEQs];
        state.bottomAnswers = [...flag4Table.bottomAnswers];
      }
      markVisited(state, layer);
    }
    state.blnChosen[layer - 1] = zeros(9);
    return;
  }

  populatePoolDraw5And4(
    state,
    layer,
    round == 3 ? flag4Data.round3 : flag4Data.round4,
    randomInt,
  );
}

/**
 * Flag 5 ("double digits by double digits", multiplication): nine on-the-fly
 * operand pairs per board, drawn from a round/layer-dependent range and
 * grouped by `modNo` exactly like flag 3's random rounds
 * (`VIFRulesMulDiv.ts:1199-1282`).
 */
function populateFlag5(
  layer: number,
  round: number,
  state: RulesMulDivProblemState,
  randomInt: (min: number, max: number) => number,
): void {
  state.topEQs = [];
  state.bottomAnswers = [];

  const [iMax, iMax2] = flag5Range(layer, round);
  let modNo = round >= 3 ? 4 : 2;
  let resMod10 = 0;
  let lastVal = 0;

  for (let p = 0; p < 9; p++) {
    let v1 = randomInt(10, iMax);
    let v2 = randomInt(10, iMax2);

    if (p % modNo == 0) {
      resMod10 = (v1 * v2) % 10;
      lastVal = v1 * v2;
    } else {
      while (
        (v1 * v2) % 10 != resMod10 ||
        lastVal == v1 * v2 ||
        (lastVal > v1 * v2 && lastVal > v1 * v2 * 2) ||
        (v1 * v2 > lastVal && v1 * v2 > lastVal * 2)
      ) {
        v1 = randomInt(10, iMax);
        v2 = randomInt(10, iMax2);
      }
    }

    if (round >= 3 && p > 4) {
      modNo = 99;
    }

    state.topEQs.push(`${v1} × ${v2}`);
    state.bottomAnswers.push(String(v1 * v2));
  }

  state.blnChosen[layer - 1] = zeros(9);
}

function flag5Range(layer: number, round: number): readonly [number, number] {
  if (round == 1) {
    return (
      [
        [49, 39],
        [69, 59],
        [89, 79],
        [99, 99],
      ] as const
    )[layer - 1]!;
  }
  if (round == 2) {
    return (
      [
        [39, 49],
        [59, 69],
        [79, 89],
        [99, 99],
      ] as const
    )[layer - 1]!;
  }
  return [99, 99];
}

/**
 * Flag 7 ("Auditory Memory, Part 1"): a single static 21-fact table, same
 * for every layer and round (`VIFRulesMulDiv.ts:1368-1377`).
 */
function populateFlag7(layer: number, state: RulesMulDivProblemState): void {
  populateStaticLayerTable(state, layer, RULES_MUL_DIV_DATA.flag7, 21);
}

/**
 * Flag 8 ("Auditory Memory, Part 2"): a single static 15-fact table, same
 * for every layer and round (`VIFRulesMulDiv.ts:1378-1387`).
 */
function populateFlag8(layer: number, state: RulesMulDivProblemState): void {
  populateStaticLayerTable(state, layer, RULES_MUL_DIV_DATA.flag8, 15);
}

/**
 * Flag 6 ("Auditory Memory, all 36 facts"): rounds 1-2 are four independent
 * static layer tables; round 3+ draws 9 facts per layer without replacement
 * from a 36-fact pool that persists across the round's four layers, reset
 * at the start of each round (`VIFRulesMulDiv.ts:1279-1368`).
 */
function populateFlag6(
  layer: number,
  round: number,
  state: RulesMulDivProblemState,
  randomInt: (min: number, max: number) => number,
): void {
  if (round == 1 || round == 2) {
    const data = RULES_MUL_DIV_DATA.flag6;
    const byRound = round == 1 ? data.round1 : data.round2;
    const table = [
      byRound.layer1,
      byRound.layer2,
      byRound.layer3,
      byRound.layer4,
    ][layer - 1]!;
    populateStaticLayerTable(state, layer, table, 9);
    return;
  }

  if (layer === 1) {
    state.pool1Used = zeros(36);
  }
  if (state.pool1.length === 0) {
    state.pool1 = [...RULES_MUL_DIV_DATA.flag6.round3.pool1];
    state.pool1Answ = [...RULES_MUL_DIV_DATA.flag6.round3.pool1Answers];
  }
  if (isFirstTime(state, layer)) {
    state.topEQs = [];
    state.bottomAnswers = [];
    for (const a of drawDistinct(9, 35, state.pool1Used, randomInt)) {
      state.topEQs.push(state.pool1[a]!);
      state.bottomAnswers.push(state.pool1Answ[a]!);
    }
    markVisited(state, layer);
  }
  state.blnChosen[layer - 1] = zeros(9);
}

/**
 * Flag 10 ("zero-one rule", division): the division mirror of flag 0 - same
 * shape (two rounds of four layers, most layers splicing in one extra
 * non-zero fact plus four zero facts), but every extra fact here is an
 * "n ÷ n = 1" pick, so - unlike flag 0's layer 1 - there is no answer-array
 * mismatch quirk to preserve; every layer here uses its own explicit answer
 * (`VIFRulesMulDiv.ts:1451-1612`).
 */
function populateFlag10(
  layer: number,
  round: number,
  state: RulesMulDivProblemState,
  randomInt: (min: number, max: number) => number,
): void {
  if (isFirstTime(state, layer)) {
    if (round == 1) {
      switch (layer) {
        case 1:
          loadPool(state, RULES_MUL_DIV_DATA.flag10.round1.layer1);
          pushDivideBySelf(
            state,
            ['2 ÷ 2', '4 ÷ 4', '6 ÷ 6', '9 ÷ 9'],
            randomInt,
          );
          appendZeroPicks(
            state,
            ['0 ÷ 1', '0 ÷ 3', '0 ÷ 8', '0 ÷ 5', '0 ÷ 7'],
            randomInt,
          );
          break;
        case 2:
          loadPool(state, RULES_MUL_DIV_DATA.flag10.round1.layer2);
          pushDivideBySelf(
            state,
            ['2 ÷ 2', '4 ÷ 4', '6 ÷ 6', '9 ÷ 9'],
            randomInt,
          );
          appendZeroPicks(
            state,
            ['0 ÷ 2', '0 ÷ 4', '0 ÷ 7', '0 ÷ 6', '0 ÷ 5'],
            randomInt,
          );
          break;
        case 3:
          loadPool(state, RULES_MUL_DIV_DATA.flag10.round1.layer3);
          pushDivideBySelf(
            state,
            ['1 ÷ 1', '9 ÷ 9', '3 ÷ 3', '6 ÷ 6'],
            randomInt,
          );
          appendZeroPicks(
            state,
            ['0 ÷ 4', '0 ÷ 2', '0 ÷ 3', '0 ÷ 9', '0 ÷ 8'],
            randomInt,
          );
          break;
        case 4:
          loadPool(state, RULES_MUL_DIV_DATA.flag10.round1.layer4);
          break;
      }
    } else if (round == 2) {
      switch (layer) {
        case 1:
          loadPool(state, RULES_MUL_DIV_DATA.flag10.round2.layer1);
          pushDivideBySelf(
            state,
            ['9 ÷ 9', '4 ÷ 4', '2 ÷ 2', '5 ÷ 5'],
            randomInt,
          );
          appendZeroPicks(
            state,
            ['0 ÷ 4', '0 ÷ 7', '0 ÷ 5', '0 ÷ 6', '0 ÷ 2'],
            randomInt,
          );
          break;
        case 2:
          loadPool(state, RULES_MUL_DIV_DATA.flag10.round2.layer2);
          break;
        case 3:
          loadPool(state, RULES_MUL_DIV_DATA.flag10.round2.layer3);
          pushDivideBySelf(
            state,
            ['8 ÷ 8', '9 ÷ 9', '3 ÷ 3', '8 ÷ 8'],
            randomInt,
          );
          appendZeroPicks(
            state,
            ['0 ÷ 2', '0 ÷ 5', '0 ÷ 6', '0 ÷ 9', '0 ÷ 7'],
            randomInt,
          );
          break;
        case 4:
          loadPool(state, RULES_MUL_DIV_DATA.flag10.round2.layer4);
          pushDivideBySelf(
            state,
            ['4 ÷ 4', '5 ÷ 5', '6 ÷ 6', '7 ÷ 7'],
            randomInt,
          );
          appendZeroPicks(
            state,
            ['0 ÷ 8', '0 ÷ 7', '0 ÷ 9', '0 ÷ 1', '0 ÷ 4'],
            randomInt,
          );
          break;
      }
    }
    markVisited(state, layer);
  }
  state.blnChosen[layer - 1] = zeros(12);
}

/** Pushes one of four inline `n ÷ n` facts - every candidate answers `1`. */
function pushDivideBySelf(
  state: RulesMulDivProblemState,
  candidates: readonly string[],
  randomInt: (min: number, max: number) => number,
): void {
  state.topEQs.push(candidates[randomInt(0, 3)]!);
  state.bottomAnswers.push('1');
}

/**
 * Records `(v1, v2)` in `state.checkPairs` if not already present, returning
 * whether it was newly recorded (i.e. whether the candidate should be
 * accepted). Shared by flags 11 and 13's within-layer de-duplication
 * (`VIFRulesMulDiv.ts:1644-1663`, `:1671-1690`, `:1769-1786`). The legacy
 * `checkPairs` starts as `[[]]` (one empty-array sentinel) rather than `[]` -
 * harmless, since `undefined == v1` never matches a real operand - so it is
 * not reproduced here.
 */
function rememberPair(
  state: RulesMulDivProblemState,
  v1: number,
  v2: number,
): boolean {
  const exists = state.checkPairs.some(([a, b]) => a == v1 && b == v2);
  if (exists) {
    return false;
  }
  state.checkPairs.push([v1, v2]);
  return true;
}

/**
 * Flag 11 (labelled "Multiply Double Digits by Tens" in the module comment,
 * but `computeOperation` reports this flag as division - preserved
 * verbatim, see `rulesMulDivCopy.ts`'s file header for the same caveat). The
 * rendered equations are genuinely multiplication (`v1 × v2`); no
 * data-file table, nine on-the-fly pairs de-duplicated within the layer
 * (`VIFRulesMulDiv.ts:1613-1692`).
 */
function populateFlag11(
  layer: number,
  round: number,
  state: RulesMulDivProblemState,
  randomInt: (min: number, max: number) => number,
): void {
  state.topEQs = [];
  state.bottomAnswers = [];
  state.checkPairs = [];

  if (round <= 2) {
    const iMin = (
      [
        [10, 20, 30, 40],
        [50, 60, 70, 80],
      ] as const
    )[round - 1]![layer - 1]!;

    let p = 0;
    while (p < 9) {
      const v1 = randomInt(10, 99);
      const v2 = p <= 3 ? iMin : iMin + 10;
      if (rememberPair(state, v1, v2)) {
        state.topEQs.push(`${v1} × ${v2}`);
        state.bottomAnswers.push(String(v1 * v2));
        p++;
      }
    }
  } else {
    let p = 0;
    while (p < 9) {
      const v1 = randomInt(10, 99);
      const v2 = randomInt(1, 9);
      if (rememberPair(state, v1, v2)) {
        state.topEQs.push(`${v1} × ${v2 * 10}`);
        state.bottomAnswers.push(String(v1 * v2 * 10));
        p++;
      }
    }
  }

  state.blnChosen[layer - 1] = zeros(9);
}

/**
 * Flag 12 ("Loose Mirror Rule", division): two rounds of two independent
 * static layer tables, no round 3+ (`VIFRulesMulDiv.ts:1693-1730`). Its
 * equation strings use the same `'P'`-delimited stacked-fraction convention
 * as AddSub's flag 8 - baked into the data, not generated here.
 */
function populateFlag12(
  layer: number,
  round: number,
  state: RulesMulDivProblemState,
): void {
  const data = RULES_MUL_DIV_DATA.flag12;
  const byRound = round == 1 ? data.round1 : data.round2;
  const table = layer == 1 ? byRound.layer1 : byRound.layer2;
  populateStaticLayerTable(state, layer, table, 9);
}

/**
 * Flag 13 ("Divide Triple Digits by Single Digits"): nine on-the-fly
 * division facts per board (`dividend = v1 * v2`, `divisor = v1`, `quotient
 * = v2`), de-duplicated within the layer exactly like flag 11
 * (`VIFRulesMulDiv.ts:1731-1788`).
 */
function populateFlag13(
  layer: number,
  round: number,
  state: RulesMulDivProblemState,
  randomInt: (min: number, max: number) => number,
): void {
  state.topEQs = [];
  state.bottomAnswers = [];
  state.checkPairs = [];

  const [iMin, iMax, iMin2, iMax2] = (
    [
      [2, 4, 11, 39],
      [4, 6, 31, 59],
      [5, 8, 41, 79],
      [2, 9, 11, 99],
    ] as const
  )[round - 1]!;

  let p = 0;
  while (p < 9) {
    const v1 = randomInt(iMin, iMax);
    const v2 = randomInt(iMin2, iMax2);
    if (rememberPair(state, v1, v2)) {
      state.topEQs.push(`${v1 * v2} ÷ ${v1}`);
      state.bottomAnswers.push(String(v2));
      p++;
    }
  }

  state.blnChosen[layer - 1] = zeros(9);
}

/**
 * Flag 15 ("Dividing Multi Digits by Double Digits"): nine on-the-fly
 * division facts per board (`dividend = v1 * v2`, `divisor = v2`, `quotient
 * = v1`), grouped by `modNo` exactly like flags 3/5's random rounds
 * (`VIFRulesMulDiv.ts:1789-1840`).
 */
function populateFlag15(
  layer: number,
  round: number,
  state: RulesMulDivProblemState,
  randomInt: (min: number, max: number) => number,
): void {
  state.topEQs = [];
  state.bottomAnswers = [];

  const [iMax, iMax2] = (
    [
      [39, 29],
      [59, 49],
      [79, 69],
      [99, 89],
    ] as const
  )[round - 1]!;
  let modNo = round >= 3 ? 4 : 2;
  let resMod10 = 0;
  let lastVal = 0;

  for (let p = 0; p < 9; p++) {
    let v1 = randomInt(11, iMax);
    let v2 = randomInt(11, iMax2);

    if (p % modNo == 0) {
      resMod10 = (v1 * v2) % 10;
      lastVal = v1 * v2;
    } else {
      while (
        (v1 * v2) % 10 != resMod10 ||
        lastVal == v1 * v2 ||
        (lastVal > v1 * v2 && lastVal > v1 * v2 * 2) ||
        (v1 * v2 > lastVal && v1 * v2 > lastVal * 2)
      ) {
        v1 = randomInt(11, iMax);
        v2 = randomInt(11, iMax2);
      }
    }

    if (round >= 3 && p > 4) {
      modNo = 99;
    }

    state.topEQs.push(`${v1 * v2} ÷ ${v2}`);
    state.bottomAnswers.push(String(v1));
  }

  state.blnChosen[layer - 1] = zeros(9);
}

/**
 * Flag 16 ("Divide 36 Facts Game"): rounds 1-2 are four independent static
 * layer tables; round 3+ draws nine facts from one 36-entry pool, bucketed
 * by answer value (9/8/7/6/other) with a fixed per-round layer rotation so
 * the rare high-quotient facts (answers 6-9) spread across the round's four
 * layers instead of clustering or exhausting their bucket
 * (`VIFRulesMulDiv.ts:1841-1988`).
 *
 * Legacy quirk, not reproduced: round 3+ also reads `this.soundPool1`/
 * `this.soundWAnswerPool1` into `factSounds`/`factAnswerSounds`, but nothing
 * in the legacy class ever assigns those two fields for flag 16 (only flag
 * 6's round 3+ branch does) - so they were always empty/`undefined` reads in
 * the original, and this port (which has no sound plumbing at all - see the
 * file header) simply has nothing to carry forward there. Legacy also
 * tallies a `countAnswer` local that is never read back for any decision -
 * omitted here as genuinely dead.
 */
function populateFlag16(
  layer: number,
  round: number,
  state: RulesMulDivProblemState,
  randomInt: (min: number, max: number) => number,
): void {
  const data = RULES_MUL_DIV_DATA.flag16;

  if (round == 1 || round == 2) {
    const byRound = round == 1 ? data.round1 : data.round2;
    const table = [
      byRound.layer1,
      byRound.layer2,
      byRound.layer3,
      byRound.layer4,
    ][layer - 1]!;
    populateStaticLayerTable(state, layer, table, 9);
    return;
  }

  if (layer == 1) {
    state.pool1Used = zeros(36);
    // A fixed rotation of layer numbers 1-4, seeded once per round: a random
    // start in [1,4], then wrapping +1 (4 wraps to 1) three more times.
    state.fixedRounds = [randomInt(1, 4)];
    for (let p = 0; p < 3; p++) {
      const previous = state.fixedRounds[p]!;
      state.fixedRounds.push(previous == 4 ? 1 : previous + 1);
    }
  }
  if (state.pool1.length == 0) {
    state.pool1 = [...data.round3.pool1];
    state.pool1Answ = [...data.round3.pool1Answers];
  }

  if (isFirstTime(state, layer)) {
    state.topEQs = [];
    state.bottomAnswers = [];
    for (let p = 0; p < 9; p++) {
      let o = flag16Bucket(p, layer, state.fixedRounds, randomInt);
      while (state.pool1Used[o] == 1) {
        o = flag16Bucket(p, layer, state.fixedRounds, randomInt);
      }
      state.pool1Used[o] = 1;
      state.topEQs.push(state.pool1[o]!);
      state.bottomAnswers.push(state.pool1Answ[o]!);
    }
    markVisited(state, layer);
  }
  state.blnChosen[layer - 1] = zeros(9);
}

/**
 * Picks which answer-value bucket cell position `p` (0-8) draws from this
 * layer. Positions 0/4 always draw a 9; 1 draws an 8; 2 draws a 7; 3 draws a
 * 6; 5/6/7 draw the harder 8/7/6 buckets only on the specific layer(s) the
 * round's `fixedRounds` rotation assigns them to (otherwise falling back to
 * "other"); 8 always falls back to "other". Preserves the legacy branching
 * exactly (`VIFRulesMulDiv.ts:1936-1976`) rather than simplifying it, since
 * the irregularity is what spreads rare facts across a round's four layers.
 */
function flag16Bucket(
  p: number,
  layer: number,
  fixedRounds: readonly number[],
  randomInt: (min: number, max: number) => number,
): number {
  const pick = (pool: readonly number[]) =>
    pool[randomInt(0, pool.length - 1)]!;

  if (p == 0 || p == 4) {
    return pick(RULES_MUL_DIV_NINES);
  }
  if (p == 1 || (p == 5 && layer != fixedRounds[3])) {
    return pick(RULES_MUL_DIV_EIGHTS);
  }
  if (
    p == 2 ||
    (p == 6 && layer != fixedRounds[1] && layer != fixedRounds[2])
  ) {
    return pick(RULES_MUL_DIV_SEVENS);
  }
  if (p == 3 || (p == 7 && layer == fixedRounds[3])) {
    return pick(RULES_MUL_DIV_SIXES);
  }
  return pick(RULES_MUL_DIV_OTHERS);
}
